/**
 * Gössl Theme - size and colour changeable per cart line (r190, SJ 05.10.2026: "sizes and colors should be able to
 * be modified in the cart"). snippets/cart-line-options.liquid renders the pickers in the cart drawer and on the
 * cart page; this file swaps the line's variant.
 *
 * Shopify cannot change the variant of a cart line, so a change is: add the new variant with the line's quantity
 * and ALL its properties (article number, delivery note, gift card recipient), then remove the old line. The add
 * goes first, so a failure never loses the piece. When Shopify adds only part of the quantity and answers 422
 * (it does that when the stock is short), the part it added is taken back out and the old line stays as it was.
 *
 * Pre-order rule (r187/r188): the new size carries the Vorbestellung plan only when it has no stock
 * (window.g26preorder.state/planFor, the same answer the quick view uses); afterwards capCart() moves every unit
 * above a size's stock onto a plan line, as after any other cart change.
 *
 * When an earlier choice (the colour) leaves the later one (the size) without a variant, the later picker shows
 * "Größe wählen" and the swap waits for it; a single remaining size is taken right away.
 * Events: 'cart:line-swapped' {error} after every attempt - the drawer redraws, the cart page reloads.
 */
(function () {
  'use strict';

  var timeoutSignal = function (ms) { return (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) ? AbortSignal.timeout(ms) : undefined; };

  function post(path, body) {
    return fetch(window.g26url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
      signal: timeoutSignal(15000)
    });
  }

  function getCart() {
    return fetch(window.g26url('cart.js'), { headers: { 'Accept': 'application/json' }, signal: timeoutSignal(15000) })
      .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }

  function planOf(line) {
    return line.selling_plan_allocation && line.selling_plan_allocation.selling_plan ? Number(line.selling_plan_allocation.selling_plan.id) : null;
  }

  function sameProps(a, b) {
    var norm = function (p) {
      var o = {};
      Object.keys(p || {}).sort().forEach(function (k) { if (p[k] !== null && p[k] !== '') o[k] = String(p[k]); });
      return JSON.stringify(o);
    };
    return norm(a) === norm(b);
  }

  function variantsOf(box) {
    var el = box.querySelector('[data-line-variants]');
    try { return JSON.parse(el ? el.textContent : '[]'); } catch (e) { return []; }
  }

  function selects(box) { return Array.prototype.slice.call(box.querySelectorAll('[data-line-option]')); }

  function paint(box) {
    var soldOut = box.getAttribute('data-sold-out') || '';
    selects(box).forEach(function (select) {
      Array.prototype.forEach.call(select.options, function (o) {
        if (!o.value) return;
        var display = o.getAttribute('data-display') || o.value;
        o.textContent = o.disabled ? display + ' – ' + soldOut : display;
      });
      var opt = select.options[select.selectedIndex];
      var value = select.parentNode.querySelector('[data-line-option-value]');
      if (value) value.textContent = opt ? (opt.getAttribute('data-display') || opt.textContent) : '';
      select.parentNode.classList.toggle('is-empty', !select.value);
    });
  }

  // re-judge every picker after `from` against the new choice before it: only values with a buyable variant
  function rejudge(box, from) {
    var variants = variantsOf(box);
    var list = selects(box);
    for (var j = from + 1; j < list.length; j++) {
      var prefix = list.slice(0, j).map(function (s) { return s.value; });
      Array.prototype.forEach.call(list[j].options, function (o) {
        if (!o.value) return;
        o.disabled = !variants.some(function (v) {
          return v[1][j] === o.value && prefix.every(function (p, k) { return v[1][k] === p; });
        });
      });
      var chosen = list[j].options[list[j].selectedIndex];
      if (!list[j].value || (chosen && chosen.disabled)) {
        var open = Array.prototype.filter.call(list[j].options, function (o) { return o.value && !o.disabled; });
        list[j].value = open.length === 1 ? open[0].value : '';
      }
    }
  }

  function setBusy(box, busy) {
    box.classList.toggle('is-busy', busy);
    selects(box).forEach(function (s) { s.disabled = busy; });
    var line = box.closest('[data-cart-item]');
    if (line) line.classList.toggle('is-updating', busy);
  }

  function finish(box, error) {
    if (error) document.dispatchEvent(new CustomEvent('cart:notice', { detail: { message: error } }));
    document.dispatchEvent(new CustomEvent('cart:line-swapped', { detail: { error: error || null, key: box.getAttribute('data-line-key') } }));
  }

  async function swap(box, variantId) {
    var key = box.getAttribute('data-line-key');
    var handle = box.getAttribute('data-handle');
    setBusy(box, true);
    try {
      var cart = await getCart();
      var line = cart.items.filter(function (i) { return i.key === key; })[0];
      if (!line) throw new Error('line gone');
      if (String(line.variant_id) === String(variantId)) { setBusy(box, false); return; }

      var g = window.g26preorder;
      var state = g && g.state ? await g.state(handle) : null;
      var plan = g && g.planFor ? g.planFor(state, variantId) : null;
      var props = line.properties || {};
      // the line the add merges into, if one exists (same variant, plan and properties)
      var same = function (i) { return String(i.variant_id) === String(variantId) && planOf(i) === (plan ? Number(plan) : null) && sameProps(i.properties, props); };
      var before = cart.items.filter(same).reduce(function (n, i) { return n + i.quantity; }, 0);

      var item = { id: Number(variantId), quantity: line.quantity };
      if (Object.keys(props).length) item.properties = props;
      if (plan) item.selling_plan = Number(plan);
      var added = await post('cart/add.js', { items: [item] });
      if (!added.ok) {
        var after = await getCart();
        var merged = after.items.filter(same);
        var now = merged.reduce(function (n, i) { return n + i.quantity; }, 0);
        if (now > before && merged.length) await post('cart/change.js', { id: merged[0].key, quantity: Math.max(0, merged[0].quantity - (now - before)) });
        throw new Error('add ' + added.status);
      }
      var removed = await post('cart/change.js', { id: key, quantity: 0 });
      if (!removed.ok) throw new Error('remove ' + removed.status);
      if (g && g.capCart) await g.capCart();
      finish(box, null);
    } catch (err) {
      console.error('Cart line change failed:', err);
      setBusy(box, false);
      finish(box, box.getAttribute('data-error'));
    }
  }

  document.addEventListener('change', function (e) {
    var select = e.target.closest ? e.target.closest('[data-line-option]') : null;
    if (!select) return;
    var box = select.closest('[data-line-options]');
    if (!box || box.classList.contains('is-busy')) return;
    var index = parseInt(select.getAttribute('data-option-index'), 10) || 0;
    rejudge(box, index);
    paint(box);
    var values = selects(box).map(function (s) { return s.value; });
    var empty = selects(box).filter(function (s) { return !s.value; })[0];
    if (empty) { empty.focus(); return; } // the next choice is the customer's
    var target = variantsOf(box).filter(function (v) {
      return values.every(function (val, k) { return v[1][k] === val; });
    })[0];
    // no buyable variant behind the choice (cannot happen through the pickers themselves): redraw the line as it is
    if (target) swap(box, target[0]);
    else finish(box, box.getAttribute('data-error'));
  });
})();
